import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import './Navigation.css';

const Navigation: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const location = useLocation();
    const { user, isAuthenticated } = useAuth();

    // Scroll detection to highlight active section
    useEffect(() => {
        const handleScroll = () => {
            // Only track scroll on the home page
            if (location.pathname !== '/') {
                setActiveSection('');
                return;
            }

            const sections = ['home', 'about', 'services', 'contact'];
            const scrollPosition = window.scrollY + 100; // Offset for better detection

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = document.getElementById(sections[i]);
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveSection(sections[i]);
                    break;
                }
            }
        };

        // Set initial active section based on hash
        if (location.hash && location.pathname === '/') {
            setActiveSection(location.hash.slice(1));
        } else if (location.pathname === '/') {
            setActiveSection('home');
        } else {
            setActiveSection('');
        }

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Run once on mount

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location.pathname, location.hash]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Helper function to check if we're on the home page
    const isHomePage = location.pathname === '/';

    // Helper function to handle navigation clicks
    const handleNavClick = (sectionId: string, e: React.MouseEvent) => {
        if (isHomePage) {
            // If on home page, smooth scroll to section
            e.preventDefault();
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            // If on other page, navigate to home with hash
            window.location.href = `/#${sectionId}`;
        }
    };

    // Helper function to get active state for nav links
    const getNavLinkClass = (sectionId: string) => {
        const baseClass = 'nav-link';
        if (isHomePage && activeSection === sectionId) {
            return `${baseClass} nav-link-active`;
        }
        return baseClass;
    };

    return (
        <nav className="navigation">
            <div className="container">
                {/* Logo and Branding */}
                <div className="nav-brand">
                    <Link to="/" className="nav-logo">
                        <div className="nav-logo-icon">
                            <i className="ri-shield-star-fill"></i>
                        </div>
                        <span className="nav-logo-text">NUROVELI</span>
                    </Link>
                </div>

                {/* Desktop Navigation Links */}
                <div className="nav-links">
                    <a
                        href="#home"
                        className={getNavLinkClass('home')}
                        onClick={(e) => handleNavClick('home', e)}
                    >
                        <i className="ri-home-4-line"></i>
                        <span>Home</span>
                    </a>
                    <a
                        href="#about"
                        className={getNavLinkClass('about')}
                        onClick={(e) => handleNavClick('about', e)}
                    >
                        <i className="ri-information-line"></i>
                        <span>About</span>
                    </a>
                    <a
                        href="#services"
                        className={getNavLinkClass('services')}
                        onClick={(e) => handleNavClick('services', e)}
                    >
                        <i className="ri-settings-3-line"></i>
                        <span>Services</span>
                    </a>
                    <a
                        href="#contact"
                        className={getNavLinkClass('contact')}
                        onClick={(e) => handleNavClick('contact', e)}
                    >
                        <i className="ri-phone-line"></i>
                        <span>Contact</span>
                    </a>
                </div>

                {/* Auth Buttons */}
                <div className="nav-auth">
                    {isAuthenticated ? (
                        // Show authenticated user options
                        <>
                            <Link to="/dashboard" className="nav-auth-btn nav-dashboard-btn">
                                <i className="ri-dashboard-3-line"></i>
                                <span>Dashboard</span>
                            </Link>
                            {(user?.role?.name === 'Admin' || user?.role?.name === 'Super Admin') && (
                                <Link to="/admin/users" className="nav-auth-btn nav-admin-btn">
                                    <i className="ri-admin-line"></i>
                                    <span>Admin</span>
                                </Link>
                            )}
                        </>
                    ) : (
                        // Show login/register for non-authenticated users
                        <>
                            <Link to="/login" className="nav-auth-btn nav-login-btn">
                                <i className="ri-login-box-line"></i>
                                <span>Login</span>
                            </Link>
                            <Link to="/register" className="nav-auth-btn nav-register-btn">
                                <i className="ri-gift-line"></i>
                                <span>Start Free Trial</span>
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile Menu Button */}
                <button
                    className={`nav-mobile-btn ${isMobileMenuOpen ? 'nav-mobile-btn-active' : ''}`}
                    onClick={toggleMobileMenu}
                    aria-label="Toggle mobile menu"
                >
                    <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
                </button>

                {/* Mobile Menu */}
                <div className={`nav-mobile-menu ${isMobileMenuOpen ? 'nav-mobile-menu-active' : ''}`}>
                    <div className="nav-mobile-links">
                        <a
                            href="#home"
                            className={`nav-mobile-link ${getNavLinkClass('home').includes('active') ? 'nav-mobile-link-active' : ''}`}
                            onClick={(e) => {
                                handleNavClick('home', e);
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <i className="ri-home-4-line"></i>
                            <span>Home</span>
                        </a>
                        <a
                            href="#about"
                            className={`nav-mobile-link ${getNavLinkClass('about').includes('active') ? 'nav-mobile-link-active' : ''}`}
                            onClick={(e) => {
                                handleNavClick('about', e);
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <i className="ri-information-line"></i>
                            <span>About</span>
                        </a>
                        <a
                            href="#services"
                            className={`nav-mobile-link ${getNavLinkClass('services').includes('active') ? 'nav-mobile-link-active' : ''}`}
                            onClick={(e) => {
                                handleNavClick('services', e);
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <i className="ri-settings-3-line"></i>
                            <span>Services</span>
                        </a>
                        <a
                            href="#contact"
                            className={`nav-mobile-link ${getNavLinkClass('contact').includes('active') ? 'nav-mobile-link-active' : ''}`}
                            onClick={(e) => {
                                handleNavClick('contact', e);
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <i className="ri-phone-line"></i>
                            <span>Contact</span>
                        </a>
                    </div>
                    <div className="nav-mobile-auth">
                        {isAuthenticated ? (
                            // Show authenticated user options
                            <>
                                <Link to="/dashboard" className="nav-mobile-auth-btn nav-mobile-dashboard-btn">
                                    <i className="ri-dashboard-3-line"></i>
                                    <span>Dashboard</span>
                                </Link>
                                {(user?.role?.name === 'Admin' || user?.role?.name === 'Super Admin') && (
                                    <Link to="/admin/users" className="nav-mobile-auth-btn nav-mobile-admin-btn">
                                        <i className="ri-admin-line"></i>
                                        <span>Admin</span>
                                    </Link>
                                )}
                            </>
                        ) : (
                            // Show login/register for non-authenticated users
                            <>
                                <Link to="/login" className="nav-mobile-auth-btn nav-mobile-login-btn">
                                    <i className="ri-login-box-line"></i>
                                    <span>Login</span>
                                </Link>
                                <Link to="/register" className="nav-mobile-auth-btn nav-mobile-register-btn">
                                    <i className="ri-user-add-line"></i>
                                    <span>Register</span>
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="nav-mobile-overlay"
                    onClick={toggleMobileMenu}
                />
            )}
        </nav>
    );
};

export default Navigation;
